// src/components/ClientListView.js
import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import neo4jService from '../services/neo4jService';
import { clientQueries } from '../repository/graphQueries'; // Ensure this path is correct

const ClientListView = () => {
    const [clientsData, setClientsData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const navigate = useNavigate();

    const fetchClientsData = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            if (!neo4jService.driver) {
                // Initialize Neo4j connection if not already done (e.g. if user lands here directly)
                // This might be redundant if Dashboard.js handles global connection
                const uri = process.env.REACT_APP_NEO4J_URI || 'neo4j://localhost:7687';
                const user = process.env.REACT_APP_NEO4J_USER || 'neo4j';
                const pass = process.env.REACT_APP_NEO4J_PASSWORD || 'password';
                await neo4jService.initialize(uri, user, pass);
            }
            const result = await neo4jService.runQuery(clientQueries.getAllClientsWithStats);
            
            setClientsData(result.map(record => ({
                name: record.name, // client_id
                goalCount: record.goalCount,
                actionItemCount: record.actionItemCount,
                blockerCount: record.blockerCount,
                initiativeCount: record.initiativeCount,
            })));
        } catch (err) {
            console.error("Error fetching client list with stats:", err);
            setError("Failed to load client data. " + err.message);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchClientsData();
    }, [fetchClientsData]);

    if (loading) {
        return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-800"></div><span className="ml-3">Loading client data...</span></div>;
    }

    if (error) {
        return <div className="p-4 bg-red-100 text-red-700 rounded-md"><strong>Error:</strong> {error} <button onClick={fetchClientsData} className="ml-2 bg-red-500 hover:bg-red-700 text-white font-bold py-1 px-2 rounded text-sm">Retry</button></div>;
    }

    if (clientsData.length === 0) {
        return <div className="p-4 text-center text-gray-500">No clients found.</div>;
    }

    return (
        <div className="container mx-auto p-4">
            <h2 className="text-2xl font-semibold text-gray-800 mb-6">Client Overview</h2>
            <div className="bg-white shadow-md rounded-lg overflow-hidden">
                <table className="min-w-full leading-normal">
                    <thead>
                        <tr className="bg-gray-100 text-left text-gray-600 uppercase text-sm">
                            <th className="px-5 py-3 border-b-2 border-gray-200">Client Name</th>
                            <th className="px-5 py-3 border-b-2 border-gray-200 text-center">Goals</th>
                            <th className="px-5 py-3 border-b-2 border-gray-200 text-center">Action Items</th>
                            <th className="px-5 py-3 border-b-2 border-gray-200 text-center">Blockers</th>
                            <th className="px-5 py-3 border-b-2 border-gray-200 text-center">Initiatives</th>
                            <th className="px-5 py-3 border-b-2 border-gray-200 text-center">Health Score</th>
                            <th className="px-5 py-3 border-b-2 border-gray-200 text-center">Trend</th>
                        </tr>
                    </thead>
                    <tbody className="text-gray-700">
                        {clientsData.map((client) => (
                            <tr key={client.name} className="border-b border-gray-200 hover:bg-gray-50 cursor-pointer" onClick={() => navigate(`/clients/${client.name}/overview`)}>
                                <td className="px-5 py-4">
                                    <Link to={`/clients/${client.name}/overview`} className="text-blue-600 hover:text-blue-800 font-medium">
                                        {client.name}
                                    </Link>
                                </td>
                                <td className="px-5 py-4 text-center">{client.goalCount}</td>
                                <td className="px-5 py-4 text-center">{client.actionItemCount}</td>
                                <td className="px-5 py-4 text-center">{client.blockerCount}</td>
                                <td className="px-5 py-4 text-center">{client.initiativeCount}</td>
                                <td className="px-5 py-4 text-center text-gray-400 italic">Not Implemented</td>
                                <td className="px-5 py-4 text-center text-gray-400 italic">Not Implemented</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

export default ClientListView;